import { EventEmitter, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReminderDto } from '@authority-labs/api-interfaces';
import { LIMIT } from '@authority-labs/constants';
import { AuthenticationService } from '@check-list/app/auth/authentication.service';
import { ChangeType } from '@check-list/core/enums/change-type.enum';
import { NotificationType } from '@check-list/core/enums/notification-type.enum';
import { CreateReminderDialogComponent } from '@check-list/shared/dialogs/create-reminder-dialog/create-reminder-dialog.component';
import { ReminderService } from '@check-list/shared/services/reminder/reminder.service';
import { GetManyDefaultResponse } from '@nestjsx/crud';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import _ from 'lodash';
import { Observable, merge, Subject, timer } from 'rxjs';
import {
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { NotificationBroadcastService } from './notification-broadcast.service';
import { TabLeaderService } from './tab-leader.service';

export interface NotificationChangedEvent {
  changedItems: ReminderDto[];
  notificationType: NotificationType;
  changeType: ChangeType;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  notificationsList: ReminderDto[] = [];

  activeNoticeCounter = 0;
  missedNoticeCounter = 0;
  private showNotificationPanelValue = false;

  changesEmitter = new EventEmitter<NotificationChangedEvent>();
  showNotification = new EventEmitter<ReminderDto>();

  openedNotificationIds: Set<number> = new Set();
  notificationQueue: Set<number> = new Set();
  private inFlightRequest$: Observable<GetManyDefaultResponse<ReminderDto>> =
    null;
  private isPolling = false;
  private rePoll$ = new Subject<void>();

  tabs: NotificationType[] = Object.values(NotificationType);
  selectedTabIndex = 1;

  constructor(
    private reminderService: ReminderService,
    private matDialog: MatDialog,
    private authService: AuthenticationService,
    private tabLeaderService: TabLeaderService,
    private broadcastService: NotificationBroadcastService
  ) {}

  get showNotificationPanel(): boolean {
    return this.showNotificationPanelValue;
  }

  set showNotificationPanel(show: boolean) {
    this.showNotificationPanelValue = show;
  }

  startPolling(): void {
    if (this.isPolling) {
      return;
    }
    this.isPolling = true;

    this.tabLeaderService.isLeader$
      .pipe(
        switchMap((isLeader) =>
          isLeader ? this.leaderPollingStream() : this.followerListenStream()
        )
      )
      .subscribe();
  }

  private leaderPollingStream(): Observable<void> {
    // Leader: poll on timer + on-demand re-polls, broadcast results
    const poll$ = merge(
      timer(0, LIMIT.NOTIFICATION_POLING_MS),
      this.rePoll$,
      this.broadcastService.onMutation$,
      this.broadcastService.onSyncRequested$
    );

    return poll$.pipe(
      switchMap(() =>
        this.setNotificationList().pipe(
          take(1),
          tap(() => {
            this.broadcastService.broadcastNotifications(
              this.notificationsList,
              this.activeNoticeCounter,
              this.missedNoticeCounter
            );
          })
        )
      ),
      map(() => undefined)
    );
  }

  private followerListenStream(): Observable<void> {
    // Follower: listen for broadcasts from leader
    this.broadcastService.requestSync();

    return this.broadcastService.onNotificationsReceived$.pipe(
      tap(({ list, activeCount, missedCount }) => {
        this.notificationsList = list;
        this.activeNoticeCounter = activeCount;
        this.missedNoticeCounter = missedCount;
        this.changesEmitter.emit({
          notificationType: NotificationType.today,
          changeType: ChangeType.receivedFromDB,
          changedItems: list
        });
        this.runNotificationHandler();
      }),
      map(() => undefined)
    );
  }

  openReminderDialog(
    reminder?: ReminderDto,
    entity?: string,
    entityId?: number
  ): Observable<ReminderDto> {
    return this.matDialog
      .open(CreateReminderDialogComponent, {
        width: '400px',
        data: { reminder, entity, entityId }
      })
      .afterClosed()
      .pipe(
        tap((notice: ReminderDto) => {
          if (notice) {
            if (reminder) {
              this.editReminder(notice, reminder);
            } else {
              this.addReminder(notice);
            }

            this.setActiveCounter();
            this.changesEmitter.emit({
              notificationType: this.getNoticeType(notice.date),
              changeType: reminder ? ChangeType.edited : ChangeType.created,
              changedItems: [notice]
            });
            this.notifyMutation();
          }
        })
      );
  }

  deleteOne(id: number): Observable<void> {
    return this.reminderService.deleteOne(id).pipe(
      tap(() => {
        const reminder = this.getReminder(id);
        if (reminder) {
          const noticeInd = this.notificationsList.findIndex(
            (n) => n.id === reminder.id
          );
          if (noticeInd > -1) {
            this.notificationsList.splice(noticeInd, 1);
          }

          this.setActiveCounter();
          this.changesEmitter.emit({
            notificationType: this.getNoticeType(reminder.date),
            changeType: ChangeType.deleted,
            changedItems: [reminder]
          });
          this.notifyMutation();
        }
      })
    );
  }

  putNoticeOff(notice: ReminderDto, min: number): Observable<ReminderDto> {
    const date = new Date();
    date.setMinutes(date.getMinutes() + min);
    return this.updateOne(notice.id, { ...notice, date }, notice);
  }

  updateOne(
    id: number,
    item: ReminderDto,
    oldItem: ReminderDto,
    queryParams: string = ''
  ): Observable<ReminderDto> {
    return this.reminderService.updateOne(id, item, queryParams).pipe(
      tap((res) => {
        this.editReminder(res, oldItem);

        this.changesEmitter.emit({
          notificationType: null,
          changeType: ChangeType.edited,
          changedItems: [item]
        });
        this.notifyMutation();
      })
    );
  }

  setNotificationList(
    isPrivate: boolean = true,
    isPublic: boolean = true
  ): Observable<GetManyDefaultResponse<ReminderDto>> {
    // Deduplicate: if a request is already in flight, return the existing one
    if (this.inFlightRequest$) {
      return this.inFlightRequest$;
    }

    const today = this.createTodayDate();
    const dateFrom = this.getDayOf(today, -30);
    const dateTo = this.getDayOf(today, 30);

    const qb = this.createQueryRequest(isPrivate, isPublic);

    qb.setFilter([
      {
        field: 'date',
        value: dateFrom.toUTCString(),
        operator: '$gte'
      },
      {
        field: 'date',
        value: dateTo.toUTCString(),
        operator: '$lt'
      }
    ]);

    this.inFlightRequest$ = this.reminderService
      .getUsersReminders(qb.query())
      .pipe(
        tap((getManyRes) => {
          const res = getManyRes.data;
          this.notificationsList = res;
          this.setActiveCounter();
          this.changesEmitter.emit({
            notificationType: NotificationType.today,
            changeType: ChangeType.receivedFromDB,
            changedItems: res
          });

          this.runNotificationHandler();
        }),
        finalize(() => {
          this.inFlightRequest$ = null;
        }),
        shareReplay(1)
      );

    return this.inFlightRequest$;
  }

  getDayOf(dateFrom: Date, increment: number = 1): Date {
    const date = this.createTodayDate();
    date.setFullYear(
      dateFrom.getFullYear(),
      dateFrom.getMonth(),
      dateFrom.getDate() + increment
    );
    return date;
  }

  private notifyMutation(): void {
    if (this.tabLeaderService.isLeader) {
      // Leader: re-poll and broadcast
      this.rePoll$.next();
    } else {
      // Follower: tell leader to re-poll
      this.broadcastService.broadcastMutation();
    }
  }

  private setActiveCounter(): void {
    this.activeNoticeCounter = 0;
    this.missedNoticeCounter = 0;
    const today = this.createTodayDate();
    this.notificationsList.forEach((notice) => {
      if (notice.isActive) {
        if (this.isToday(notice.date)) {
          this.activeNoticeCounter++;
        } else if (new Date(notice.date) < today) {
          this.missedNoticeCounter++;
        }
      }
    });
  }

  isToday(date: Date): boolean {
    const today = this.createTodayDate();
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    return today.toUTCString() === checkDate.toUTCString();
  }

  private getReminder(id: number): ReminderDto {
    return this.notificationsList.find((reminder) => reminder.id === id);
  }

  private editReminder(
    newReminder: ReminderDto,
    oldReminder: ReminderDto
  ): void {
    // was created/updated reminder for other user
    if (newReminder.recipientName !== this.authService.getUsername()) {
      this.removeReminder(newReminder);
    }
    // on date change
    else if (oldReminder.date !== newReminder.date) {
      this.removeReminder(newReminder);
      this.setReminderByDate(newReminder);
    }
    // on date details change
    else {
      const oldNoticeInd = this.notificationsList.findIndex(
        (n) => n.id === oldReminder.id
      );
      const ind =
        oldNoticeInd > -1 ? oldNoticeInd : this.notificationsList.length;
      this.notificationsList.splice(ind, 1, newReminder);
    }
    this.setActiveCounter();
  }

  private addReminder(newReminder: ReminderDto): void {
    if (newReminder.recipientName === this.authService.getUsername()) {
      this.setReminderByDate(newReminder);
    }
  }

  private runNotificationHandler(): void {
    const now = Date.now();
    this.notificationsList.forEach((notice) => {
      if (
        notice.isActive &&
        this.isToday(notice.date) &&
        +new Date(notice.date) - now < 0 &&
        !this.notificationQueue.has(notice.id)
      ) {
        this.notificationQueue.add(notice.id);
      }
    });

    if (this.openedNotificationIds.size < LIMIT.MAX_NOTIFICATION_QUEUE_SIZE) {
      [...this.notificationQueue]
        .slice(0, LIMIT.MAX_NOTIFICATION_QUEUE_SIZE)
        .forEach((reminderId) => this.sendOpenNoticePopupEvent(reminderId));
    }
  }

  onPopupNoticeClose(notice: ReminderDto) {
    this.notificationQueue.delete(notice.id);
    this.openedNotificationIds.delete(notice.id);
    if (this.notificationQueue.size) {
      [...this.notificationQueue].find((reminderId) =>
        this.sendOpenNoticePopupEvent(reminderId)
      );
    }
  }

  sendOpenNoticePopupEvent(reminderId: number): boolean {
    let wasNoticeShown = false;
    if (!this.openedNotificationIds.has(reminderId)) {
      const notice = this.notificationsList.find((i) => i.id === reminderId);
      if (notice) {
        this.openedNotificationIds.add(notice.id);
        this.showNotification.emit(notice);
        wasNoticeShown = true;
      }
    }

    return wasNoticeShown;
  }

  private createQueryRequest(isPrivate: boolean, isPublic: boolean) {
    let search;
    if (isPrivate !== isPublic) {
      search = { isPrivate: { $eq: isPrivate } };
    }
    return RequestQueryBuilder.create({
      search
    });
  }

  createTodayDate(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  private getNoticeType(date: Date) {
    if (this.isToday(date)) {
      return NotificationType.today;
    } else {
      return date >= this.getDayOf(new Date(), 0)
        ? NotificationType.all
        : NotificationType.missed;
    }
  }

  private setReminderByDate(newReminder: ReminderDto) {
    const indPrevItem = this.notificationsList.findIndex(
      (i) => i.date > newReminder.date
    );
    if (indPrevItem > -1) {
      this.notificationsList.splice(indPrevItem, 0, newReminder);
    } else {
      this.notificationsList.push(newReminder);
    }
  }

  private removeReminder(newReminder: ReminderDto) {
    _.remove(this.notificationsList, (i) => i.id === newReminder.id);
  }

  getRemindersOnDate(date: Date, activeOnly: boolean = false): ReminderDto[] {
    return this.notificationsList.filter((i) => {
      if (activeOnly && !i.isActive) {
        return false;
      }
      const itemDate = new Date(i.date);
      return (
        itemDate.getFullYear() === date.getFullYear() &&
        itemDate.getMonth() === date.getMonth() &&
        itemDate.getDate() === date.getDate()
      );
    });
  }

  showNotificationPanelByDate(date: Date) {
    const noticePanelType = this.getNoticeType(date);
    this.selectedTabIndex = this.tabs.findIndex((i) => i === noticePanelType);
    this.showNotificationPanel = true;
  }
}
